import { useState, useEffect, useRef, useCallback } from 'react';
import { getClientsDashboard, getClientDrafts, updateClient } from '@/lib/apiService';
import { Client, ClientDashboardItem, ClientRosterStatusFilter } from '@/types/client';
import { PaginationInfo } from '@/types/common';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';

type ClientStatusTotals = {
  all: number;
  active: number;
  suspended: number;
  drafts: number;
  pendingDeletion: number;
};

const EMPTY_STATUS_TOTALS: ClientStatusTotals = {
  all: 0,
  active: 0,
  suspended: 0,
  drafts: 0,
  pendingDeletion: 0,
};

export const useClients = (
  initialStatus: ClientRosterStatusFilter = 'active',
  options: { initialPage?: number; initialLimit?: number } = {}
) => {
  const initialPageRef = useRef(Math.max(1, options.initialPage ?? 1));
  const initialLimitRef = useRef(Math.max(1, options.initialLimit ?? 10));
  const [clients, setClients] = useState<ClientDashboardItem[]>([]);
  const [filteredClients, setFilteredClients] = useState<ClientDashboardItem[]>([]);
  const [paginatedClients, setPaginatedClients] = useState<ClientDashboardItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [clientStatus, setClientStatus] = useState<ClientRosterStatusFilter>(initialStatus);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: initialPageRef.current,
    limit: initialLimitRef.current,
    total: 0,
    pages: 0
  });
  const [currentPage, setCurrentPage] = useState(initialPageRef.current);
  const [statusTotals, setStatusTotals] = useState<ClientStatusTotals>(EMPTY_STATUS_TOTALS);
  const fetchGenerationRef = useRef(0);
  const clientStatusRef = useRef(clientStatus);
  const initialFetchPendingRef = useRef(true);
  const pageResetEffectInitializedRef = useRef(false);
  const paginationLimitRef = useRef(initialLimitRef.current);
  clientStatusRef.current = clientStatus;
  paginationLimitRef.current = pagination.limit;

  // Get unique locations for filter
  const locations = Array.from(new Set(clients.map(client => client.location).filter(Boolean))) as string[];

  const clearVisibleClients = useCallback(() => {
    setClients([]);
    setFilteredClients([]);
    setPaginatedClients([]);
  }, []);

  const changeClientStatus = useCallback((status: ClientRosterStatusFilter) => {
    if (status === clientStatusRef.current) return;
    clientStatusRef.current = status;
    setClientStatus(status);
    setLoading(true);
    setError(null);
    setCurrentPage(1);
    clearVisibleClients();
  }, [clearVisibleClients]);

  // Fetch clients from API. Drafts use a dedicated endpoint/data source — never the Active/All Clients API.
  const fetchClients = useCallback(async (page: number = 1, limit: number = paginationLimitRef.current) => {
    const generation = ++fetchGenerationRef.current;
    const requestedStatus = clientStatusRef.current;
    try {
      setLoading(true);
      setError(null);
      const searchParam = searchQuery.trim() || undefined;

      const response = requestedStatus === 'draft'
        ? await getClientDrafts({ page, limit, search: searchParam })
        : await getClientsDashboard({
            page,
            limit,
            status: requestedStatus,
            search: searchParam,
          });

      if (generation !== fetchGenerationRef.current) return;

      setClients(response.clients as ClientDashboardItem[]);
      setPagination(response.pagination);
      setStatusTotals({
        all: response.counts?.all ?? 0,
        active: response.counts?.active ?? 0,
        suspended: response.counts?.suspended ?? 0,
        drafts: response.counts?.drafts ?? 0,
        pendingDeletion: response.counts?.pendingDeletion ?? 0,
      });
      setCurrentPage(response.pagination?.page ?? page);
    } catch (err: unknown) {
      if (generation !== fetchGenerationRef.current) return;
      console.error('Error fetching clients:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch clients');
      showErrorToast(err, "Failed to fetch clients");
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [searchQuery]);

  // Fetch from one controlled path so the Drafts tab does not double-load on mount
  // through separate status/search effects.
  useEffect(() => {
    const page = initialFetchPendingRef.current ? initialPageRef.current : 1;
    const limit = initialFetchPendingRef.current ? initialLimitRef.current : paginationLimitRef.current;
    initialFetchPendingRef.current = false;
    setCurrentPage(page);

    const timeoutId = setTimeout(() => {
      void fetchClients(page, limit);
    }, searchQuery.trim() ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [clientStatus, searchQuery, fetchClients]);

  // Reset to page 1 when location filter changes
  useEffect(() => {
    if (!pageResetEffectInitializedRef.current) {
      pageResetEffectInitializedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [locationFilter, clientStatus]);

  useEffect(() => {
    // Since status and search are now filtered server-side, we only need to filter by location
    // Preserve the backend order - no sorting applied
    const filtered = clients.filter(client => {
      // Filter by location
      const matchesLocation = 
        locationFilter === "" || 
        (client.location && client.location.includes(locationFilter));
      
      return matchesLocation;
    });
    
    // Preserve the backend order - no sorting
    setFilteredClients(filtered);
    
    // Update pagination for filtered results when filtering by location
    if (locationFilter !== "") {
      const totalFiltered = filtered.length;
      const limit = pagination.limit;
      const pages = Math.ceil(totalFiltered / limit) || 1;
      setPagination(prev => ({
        ...prev,
        total: totalFiltered,
        pages: pages
      }));
    }
  }, [locationFilter, clients, pagination.limit, clientStatus]);

  // Paginate filtered clients when filtering by location, or use all clients when not
  useEffect(() => {
    if (locationFilter !== "") {
      // Client-side pagination for filtered results
      const startIndex = (currentPage - 1) * pagination.limit;
      const endIndex = startIndex + pagination.limit;
      const paginated = filteredClients.slice(startIndex, endIndex);
      setPaginatedClients(paginated);
    } else {
      // When not filtering by location, use filteredClients as-is (already paginated by backend)
      setPaginatedClients(filteredClients);
    }
  }, [filteredClients, currentPage, pagination.limit, locationFilter]);

  const handleAddClient = (clientData: Partial<Client>) => {
    // Generate a unique ID (in a real app, the server would do this)
    const newId = (Math.max(0, ...clients.map(c => parseInt(c.id) || 0)) + 1).toString();
    const eerValue = clientData.eer ?? clientData.eerCalories ?? 0;

    // Create a new client with default values for missing fields (client-side placeholder; server would create in real flow)
    const newClient: Client = {
      id: newId,
      schemaVersion: 2,
      firstName: clientData.firstName || "",
      lastName: clientData.lastName || "",
      email: clientData.email || "",
      phone: clientData.phone ?? null,
      location: clientData.location || "",
      status: "active" as const,
      nutritionistId: "",
      phoneCountryCode: "",
      fullName: `${clientData.firstName || ""} ${clientData.lastName || ""}`.trim() || "",
      targetWeightKg: null,
      heightCm: 0,
      weightKg: 0,
      dateOfBirth: "",
      gender: "male" as Client["gender"],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      bmiCategory: "normal" as Client["bmiCategory"],
      bmiCategoryDisplay: "",
      bmi: clientData.bmi ?? 0,
      bmr: clientData.bmr ?? null,
      eer: eerValue,
      eerWithoutGrowthFactor: null,
      palUsed: null,
      calculationDetails: {
        formulaUsed: "",
        formulaDisplayName: "",
        palUsed: null,
        weightGoalAdjustment: 0,
        growthFactorApplied: false,
        safetyFloorApplied: false,
        source: "bmr_x_pal",
        calculatedAt: new Date().toISOString(),
      },
      activityLevel: "",
      applyGrowthFactor: false,
      pregnancyTrimester: "none" as Client["pregnancyTrimester"],
      isTwinPregnancy: false,
      lactationPeriod: "none" as Client["lactationPeriod"],
      lactationGoal: null,
      bmrFormulaId: "henry_oxford" as Client["bmrFormulaId"],
      weightGoal: null,
      waistCircumferenceCm: null,
      hipCircumferenceCm: null,
      bodyFatPercentage: null,
      medicalConditions: null,
      medications: null,
      notes: null,
      nutritionistNotes: null,
      allergies: [],
      dietaryPreferences: [],
      healthGoals: [],
      preferredMeasurementSystem: "metric" as Client["preferredMeasurementSystem"],
      preferredContactMethod: "email" as Client["preferredContactMethod"],
      source: "manual",
      lastInteractionAt: null,
    };
    
    // Add the new client
    setClients(prevClients => [...prevClients, newClient]);
    
    showSuccessToast("Success", `${newClient.firstName} ${newClient.lastName} has been added as a client.`);
  };

  const handleReactivateClient = async (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    try {
      await updateClient(clientId, { status: 'active' });
      showSuccessToast(
        'Client Reactivated',
        client
          ? `${client.fullName || client.id} has been moved to Active Clients.`
          : 'Client has been reactivated successfully.'
      );
      changeClientStatus('active');
    } catch (err: unknown) {
      showErrorToast(err, 'Failed to reactivate client');
    }
  };

  const handleSuspendClient = async (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    try {
      await updateClient(clientId, { status: 'inactive' });
      showSuccessToast(
        'Client Suspended',
        client
          ? `${client.fullName || client.id} has been moved to Suspended Clients.`
          : 'Client has been suspended successfully.'
      );
      changeClientStatus('suspended');
    } catch (err: unknown) {
      showErrorToast(err, 'Failed to suspend client');
    }
  };

  return {
    clients,
    filteredClients: paginatedClients, // Return paginated clients for display
    clientStatus,
    searchQuery,
    locationFilter,
    locations,
    loading,
    error,
    pagination,
    currentPage,
    statusTotals,
    setSearchQuery,
    setLocationFilter,
    setClientStatus: changeClientStatus,
    handleAddClient,
    handleReactivateClient,
    handleSuspendClient,
    fetchClients
  };
};
